import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  type DocumentReference,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { Order, OrderStatus, Quote, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot } from '@shared/utils';
import { generateOrderNo, writeAuditLog } from '@shared/utils';
import {
  fetchQuote,
  fetchQuotes,
  fetchQuoteItems,
  duplicateQuoteWithItems,
  deleteQuote,
  nextOrderQuoteBranch,
} from '@modules/quote';
import { fetchCustomer } from '@modules/customer';
import { fetchLimitTypes, fetchPaymentDates } from '@modules/master';
import type { PartnerSnapshot } from '@shared/types';
import { snapshotFromMasters } from './customer-snapshot';
import { nextMonthTitle, shiftTimestampOneMonth } from '../utils/month-shift';

/**
 * 顧客IDから締め/支払マスタを名称解決した顧客スナップショットを生成する（Mr.販管「依頼元」仕様）。
 * 顧客が見つからない場合は null。
 */
async function buildOrderSnapshot(customerId: ID): Promise<PartnerSnapshot | null> {
  const customer = await fetchCustomer(customerId);
  if (!customer) return null;
  const [limitTypes, paymentDates] = await Promise.all([fetchLimitTypes(), fetchPaymentDates()]);
  return snapshotFromMasters(customer, limitTypes, paymentDates);
}

const orderCreateSchema = z.object({
  customerId: z.string().min(1),
  contactId: z.string().nullable().default(null),
  quoteId: z.string().nullable().default(null),
  title: z.string().min(1, '件名は必須です'),
  deliveryDate: z
    .custom<import('firebase/firestore').Timestamp | null>(
      (v) => v === null || typeof (v as Record<string, unknown>)['seconds'] === 'number'
    )
    .nullable()
    .default(null),
  assignedUserId: z.string().min(1),
  notes: z.string().nullable().default(null),
  // B3追加
  businessTypeId: z.string().nullable().default(null),
  usergroupId: z.string().nullable().default(null),
  taxCalcMode: z.enum(['floor', 'ceil', 'round', 'manual']).default('floor'),
  // AI秘書の書込ツールだけが true を渡す（本体UIのフォームはこのフィールドを送らない）。
  // 人がレビュー確定すると deleteField() で消える。docs/AI_SECRETARY_TOOL_ARCHITECTURE.md §3.5
  aiPendingReview: z.boolean().optional(),
});

const orderUpdateSchema = orderCreateSchema.extend({
  status: z.enum(['quote', 'in_progress', 'completed', 'lost']).optional(),
}).partial();

export type OrderCreateInput = z.infer<typeof orderCreateSchema>;
export type OrderUpdateInput = Partial<Omit<OrderCreateInput, 'customerId'>> & { status?: OrderStatus };

const COL = 'orders';

export async function fetchOrders(filters?: {
  customerId?: ID;
  status?: OrderStatus;
  assignedUserId?: ID;
}): Promise<Order[]> {
  let q = query(collection(db, COL), where('isDeleted', '==', false), orderBy('createdAt', 'desc'), limit(500));
  if (filters?.customerId) q = query(q, where('customerId', '==', filters.customerId));
  if (filters?.status) q = query(q, where('status', '==', filters.status));
  if (filters?.assignedUserId) q = query(q, where('assignedUserId', '==', filters.assignedUserId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Order);
}

export async function fetchOrder(id: ID): Promise<Order | null> {
  return fromSnapshot<Order>(await getDoc(doc(db, COL, id)));
}

export async function createOrder(data: OrderCreateInput, userId: ID): Promise<Order> {
  const validated = parseOrThrow(orderCreateSchema, data);
  const orderNo = await generateOrderNo();
  const customerSnapshot = await buildOrderSnapshot(validated.customerId);
  const payload = {
    ...validated,
    code: orderNo,
    orderNo,
    status: 'quote' as OrderStatus,
    subtotal: 0,
    taxAmount: 0,
    total: 0,
    quoteTotal: 0,
    invoiceTotal: 0,
    subcontractTotal: 0,
    procurementTotal: 0,
    grossProfit: 0,
    grossProfitRate: 0,
    masterLock: false,
    customerSnapshot,
    ...buildBaseFields(userId),
  };
  const ref = await addDoc(collection(db, COL), payload);
  await writeAuditLog({
    entityType: 'order',
    entityId: ref.id,
    action: 'create',
    userId,
    after: validated,
  });
  return { id: ref.id, ...payload } as unknown as Order;
}

export async function createOrderFromQuote(quoteId: ID, userId: ID): Promise<Order> {
  const quote = await fetchQuote(quoteId);
  if (!quote) throw new Error('見積が見つかりません。');
  const items = await fetchQuoteItems(quoteId);

  const customerSnapshot = await buildOrderSnapshot(quote.customerId);

  const orderNo = await generateOrderNo();
  const payload = {
    code: orderNo,
    orderNo,
    quoteId,
    customerId: quote.customerId,
    contactId: quote.contactId,
    title: quote.title,
    status: 'in_progress' as OrderStatus,
    deliveryDate: null,
    subtotal: quote.subtotal,
    taxAmount: quote.taxAmount,
    total: quote.total,
    assignedUserId: quote.assignedUserId,
    notes: quote.notes,
    customerSnapshot,
    masterLock: false,
    businessTypeId: null,
    usergroupId: null,
    taxCalcMode: 'floor' as const,
    quoteTotal: quote.total,
    invoiceTotal: 0,
    subcontractTotal: 0,
    procurementTotal: 0,
    grossProfit: 0,
    grossProfitRate: 0,
    ...buildBaseFields(userId),
  };
  const ref = await addDoc(collection(db, COL), payload);
  await writeAuditLog({
    entityType: 'order',
    entityId: ref.id,
    action: 'create',
    userId,
    after: { fromQuote: quoteId, itemCount: items.length },
  });
  return { id: ref.id, ...payload } as unknown as Order;
}

/**
 * 案件（受注）の複製対象となる見積を集める。
 * - 案件と見積の紐づけの正は `quote.orderId`（案件詳細の見積書管理タブと同じ基準）。
 *   案件内には枝番付きの見積を複数持てるため、単一ポインタの `order.quoteId` だけでは
 *   取りこぼす（見積書複写・版作成・AI秘書からの作成は `order.quoteId` を更新しない）。
 * - 無効（disabled）の版は破棄済みのため複製しない。
 * - 旧データ互換: `order.quoteId` の見積が `orderId` 未設定で拾えない場合のみ先頭に加える。
 * - 並びは枝番（code）昇順にそろえ、複製先でも同じ順で採番されるようにする。
 */
async function collectQuotesToDuplicate(orderId: ID, legacyQuoteId: ID | null): Promise<Quote[]> {
  // includeDisabled で取得してから無効を落とす。disabled を Firestore の where に足すと
  // (isDeleted, orderId, disabled, createdAt) の複合インデックスが必要になり、未作成のため失敗する。
  const linked = await fetchQuotes({ orderId, includeDisabled: true });
  const quotes = linked.filter((q) => !q.disabled);
  if (legacyQuoteId && !quotes.some((q) => q.id === legacyQuoteId)) {
    const legacy = await fetchQuote(legacyQuoteId);
    // 他の案件に紐づいた見積を指している場合は対象外（この案件の見積ではない）
    const belongsHere = legacy && (!legacy.orderId || legacy.orderId === orderId);
    if (legacy && belongsHere && !legacy.disabled) quotes.push(legacy);
  }
  // numeric 比較で 0001-9 < 0001-10 の順にそろえる（単純な文字列比較では逆転する）
  return quotes.sort((a, b) => (a.code ?? '').localeCompare(b.code ?? '', undefined, { numeric: true }));
}

/**
 * 複製元の見積を、新しい案件に紐づけた状態で1件ずつ複製する。
 * - 最初から新案件・新枝番「受注番号-連番」(0001-01) で作る。作成後に orderId を貼り替える方式だと、
 *   途中で失敗したときに複製した見積が複製元の案件へ紛れ込んで残る。
 * - 採番は新案件内の既存枝番を数えるため、必ず1件ずつ順番に処理する（並列化すると枝番が重複する）。
 * - 途中で失敗した場合は、この複製で作った見積と受注をソフト削除して「失敗＝何も残らない」に
 *   そろえたうえで、どこまで進んだかを監査ログに残し、例外を呼び出し元（UI）へ伝える。
 *   後始末や監査ログの失敗で元の例外を隠さない。
 */
async function duplicateLinkedQuotes(
  sourceQuotes: Quote[],
  orderRef: DocumentReference,
  orderNo: string,
  userId: ID,
  context: { sourceOrderId: ID; shiftMonth: boolean }
): Promise<{ id: ID; grandtotal: number }[]> {
  const duplicated: { id: ID; grandtotal: number }[] = [];
  try {
    for (const q of sourceQuotes) {
      const code = await nextOrderQuoteBranch(orderRef.id, orderNo);
      duplicated.push(
        await duplicateQuoteWithItems(q.id, userId, { shiftMonth: context.shiftMonth, orderId: orderRef.id, code })
      );
    }
    return duplicated;
  } catch (e) {
    for (const d of duplicated) {
      await deleteQuote(d.id, userId).catch(() => undefined);
    }
    await updateDoc(orderRef, { isDeleted: true, ...buildUpdateFields(userId) }).catch(() => undefined);
    await writeAuditLog({
      entityType: 'order',
      entityId: orderRef.id,
      action: 'delete',
      userId,
      after: {
        duplicatedFrom: context.sourceOrderId,
        shiftMonth: context.shiftMonth,
        quotesExpected: sourceQuotes.length,
        quotesDuplicated: duplicated.length,
        partialFailure: true,
        rolledBack: true,
      },
    }).catch(() => undefined);
    throw e;
  }
}

/**
 * 受注を複製する（Mr.販管「案件複製」相当）。案件に紐づく見積もすべて明細ごと複製する。
 * - 受注日（createdAt）は複製した当日が自動で入る。
 * - shiftMonth=false（通常複製）: 案件名に「 コピー」を付与し、月表記・日付はそのまま。
 * - shiftMonth=true（翌月複製）: 案件名・見積・明細の月表記（例「5月度」）を翌月へ自動シフトし、
 *   納期も1ヶ月後へずらす。毎月決まって請求する保守費用などの翌月分を一括生成する用途。
 * いずれもステータスは「見積り」で起票し、請求・外注・仕入の実績集計は0へリセットする。
 */
export async function duplicateOrder(
  sourceId: ID,
  userId: ID,
  options: { shiftMonth: boolean }
): Promise<Order> {
  const source = await fetchOrder(sourceId);
  if (!source) throw new Error('受注が見つかりません。');

  const title = options.shiftMonth ? nextMonthTitle(source.title) : `${source.title} コピー`;
  const deliveryDate = options.shiftMonth ? shiftTimestampOneMonth(source.deliveryDate) : source.deliveryDate;

  // 複製対象の見積は、受注を作る前に確定させておく（複製元の状態を読むため）
  const sourceQuotes = await collectQuotesToDuplicate(sourceId, source.quoteId ?? null);

  const orderNo = await generateOrderNo();
  // 複製は「コピー」のため、複製元の凍結スナップショット（受注時点の宛先・締め/支払）をそのまま引き継ぐ。
  // 顧客マスタを再取得しないことで、複製元の顧客が論理削除済み（isDeleted=true）でセキュリティルール上
  // 単一ドキュメント read が拒否されるケースでも複製を継続できる。スナップショット未保持の旧データのみ
  // フォールバックで再構築を試み、失敗しても複製は止めない。
  let customerSnapshot = source.customerSnapshot ?? null;
  if (!customerSnapshot) {
    try {
      customerSnapshot = await buildOrderSnapshot(source.customerId);
    } catch {
      customerSnapshot = null;
    }
  }
  const payload = {
    code: orderNo,
    orderNo,
    // 見積は受注を作ってから複製・連結するため、ここでは未連結で起票する（下で確定値に更新する）
    quoteId: null as ID | null,
    customerId: source.customerId,
    contactId: source.contactId,
    title,
    status: 'quote' as OrderStatus,
    deliveryDate,
    // 金額は元の見積額をそのまま引き継ぐ（月シフトでは金額は変わらない）
    subtotal: source.subtotal,
    taxAmount: source.taxAmount,
    total: source.total,
    assignedUserId: source.assignedUserId,
    notes: source.notes,
    customerSnapshot,
    masterLock: false,
    businessTypeId: source.businessTypeId,
    usergroupId: source.usergroupId,
    taxCalcMode: source.taxCalcMode,
    // 見積を複製したら合算値へ更新する。見積が無い案件は複製元の値をそのまま引き継ぐ。
    quoteTotal: source.quoteTotal ?? 0,
    // 実績系（請求・外注・仕入）は新規案件として0へリセット
    invoiceTotal: 0,
    subcontractTotal: 0,
    procurementTotal: 0,
    grossProfit: 0,
    grossProfitRate: 0,
    ...buildBaseFields(userId),
  };
  const ref = await addDoc(collection(db, COL), payload);

  const duplicatedQuotes = await duplicateLinkedQuotes(sourceQuotes, ref, orderNo, userId, {
    sourceOrderId: sourceId,
    shiftMonth: options.shiftMonth,
  });

  // 受注の quoteId は先頭（枝番が最も若い）見積を指す旧来の単一ポインタ。互換のため維持する。
  // 見積額は複製した見積の税込合計の合算（案件詳細の見積書管理タブの集計と同じ基準）。
  const quoteId: ID | null = duplicatedQuotes[0]?.id ?? null;
  const quoteTotal = duplicatedQuotes.reduce((sum, d) => sum + d.grandtotal, 0);
  if (duplicatedQuotes.length > 0) {
    await updateDoc(ref, { quoteId, quoteTotal, ...buildUpdateFields(userId) });
  }

  await writeAuditLog({
    entityType: 'order',
    entityId: ref.id,
    action: 'create',
    userId,
    after: {
      duplicatedFrom: sourceId,
      shiftMonth: options.shiftMonth,
      quoteId,
      quoteIds: duplicatedQuotes.map((d) => d.id),
    },
  });
  return {
    id: ref.id,
    ...payload,
    ...(duplicatedQuotes.length > 0 ? { quoteId, quoteTotal } : {}),
  } as unknown as Order;
}

export async function updateOrder(id: ID, data: OrderUpdateInput, userId: ID): Promise<Order> {
  const validated = parseOrThrow(orderUpdateSchema, data);
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('受注が見つかりません。');
  // 顧客が変更された場合はスナップショットを取り直す（Mr.販管「依頼元」仕様）
  const current = snap.data() as Order;
  const customerSnapshot =
    validated.customerId && validated.customerId !== current.customerId
      ? await buildOrderSnapshot(validated.customerId)
      : undefined;
  const patch = {
    ...validated,
    ...(customerSnapshot !== undefined ? { customerSnapshot } : {}),
    ...buildUpdateFields(userId),
  };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'order',
    entityId: id,
    action: 'update',
    userId,
    before: snap.data(),
    after: validated,
  });
  return { id, ...snap.data(), ...patch } as unknown as Order;
}

export async function updateOrderStatus(id: ID, status: OrderStatus, userId: ID): Promise<Order> {
  return updateOrder(id, { status } as OrderUpdateInput, userId);
}

// ── 帳票の操作から案件の進行状態を自動で進める ──────────────────────
// 手動更新の忘れを防ぐのが目的。ただし「完了」「失注」は人が決めた終着点なので、
// 帳票の副作用で勝手に巻き戻さない（完了した案件に追加納品しても進行中には戻さない）。
// 案件が見つからなくても例外にしない（帳票の作成・発行そのものを失敗させないため）。

/**
 * 案件を「進行中」へ進める。納品書・請求書を作成したときに呼ぶ。
 * 進めるのは「見積り」段階の案件だけ。
 */
export async function advanceOrderToInProgress(
  orderId: ID | null | undefined,
  userId: ID
): Promise<void> {
  if (!orderId) return; // 案件に紐づかない単独帳票
  const order = await fetchOrder(orderId);
  if (!order || order.status !== 'quote') return;
  await updateOrderStatus(orderId, 'in_progress', userId);
}

/**
 * 案件を「完了」にする。請求書を発行したときに呼ぶ。
 * 失注・完了済みの案件は動かさない。
 *
 * ※「完了にしてよいか」の判定（案件に未発行の請求書が残っていないか）は**呼び出し側**が行う。
 *   請求書を数えるのは invoice 側の責務であり、order から invoice を参照すると循環するため。
 */
export async function completeOrder(orderId: ID | null | undefined, userId: ID): Promise<void> {
  if (!orderId) return;
  const order = await fetchOrder(orderId);
  if (!order) return;
  if (order.status === 'completed' || order.status === 'lost') return;
  await updateOrderStatus(orderId, 'completed', userId);
}

/**
 * 完了した案件を「進行中」へ戻す。請求書の発行を取り消したときに呼ぶ。
 *
 * 完了にしたのは「請求が出揃った」という自動判定なので、その前提が崩れたら
 * 自動化が戻すのが筋。そうしないと「未発行の請求書がある完了案件」という
 * 矛盾した状態が残る。
 * 戻すのは「完了」の案件だけ。失注は人が下した判断なので機械が覆さない。
 */
export async function reopenOrder(orderId: ID | null | undefined, userId: ID): Promise<void> {
  if (!orderId) return;
  const order = await fetchOrder(orderId);
  if (!order || order.status !== 'completed') return;
  await updateOrderStatus(orderId, 'in_progress', userId);
}

export async function updateOrderAggregates(
  id: ID,
  aggregates: {
    quoteTotal?: number;
    invoiceTotal?: number;
    subcontractTotal?: number;
    procurementTotal?: number;
  },
  userId: ID
): Promise<void> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('受注が見つかりません。');
  const current = snap.data() as Order;

  const quoteTotal = aggregates.quoteTotal ?? current.quoteTotal ?? 0;
  const invoiceTotal = aggregates.invoiceTotal ?? current.invoiceTotal ?? 0;
  const subcontractTotal = aggregates.subcontractTotal ?? current.subcontractTotal ?? 0;
  const procurementTotal = aggregates.procurementTotal ?? current.procurementTotal ?? 0;

  const grossProfit = invoiceTotal - subcontractTotal - procurementTotal;
  const grossProfitRate = invoiceTotal > 0 ? grossProfit / invoiceTotal : 0;

  const patch = {
    quoteTotal,
    invoiceTotal,
    subcontractTotal,
    procurementTotal,
    grossProfit,
    grossProfitRate,
    ...buildUpdateFields(userId),
  };

  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'order',
    entityId: id,
    action: 'update',
    userId,
    before: snap.data(),
    after: { aggregates: patch },
  });
}

/**
 * 受注に内包される子ドキュメントを保持するコレクション一覧。
 * いずれも `orderId` で親受注に連結され、`isDeleted` でソフト削除される。
 * 受注（親）を削除する際は、これらの子（見積書・納品書・注文書・請求書・外注支払い・仕入れ）も
 * まとめてソフト削除し、一覧上の不整合を防ぐ。
 */
const ORDER_CHILD_COLLECTIONS = [
  'quotes', // 見積書
  'deliveries', // 納品書
  'purchase_orders', // 注文書
  'invoices', // 請求書
  'subcontract_orders', // 外注支払い
  'procurement_orders', // 仕入れ
] as const;

// Firestore の writeBatch は1コミット500件が上限。安全マージンを取って分割する。
const BATCH_LIMIT = 450;

/**
 * 受注に紐づく請求書のうち、月次締め処理でロック済み（isLocked=true）のものが
 * 1件でもあれば削除を拒否する。締め後のデータ整合性を守るためのガード。
 * 単一フィールド（orderId）でクエリし、ロック/削除済み判定はメモリ上で行う
 * （複合インデックス不要にするため）。
 */
async function assertOrderNotLocked(orderId: ID): Promise<void> {
  const snap = await getDocs(query(collection(db, 'invoices'), where('orderId', '==', orderId)));
  const hasLocked = snap.docs.some((d) => {
    const data = d.data();
    return data['isDeleted'] !== true && data['isLocked'] === true;
  });
  if (hasLocked) {
    throw new Error('月次締め処理でロックされた請求書が紐づいているため、この受注は削除できません。');
  }
}

/**
 * 受注に紐づく未削除の子ドキュメント参照をすべて収集する。
 */
async function collectOrderChildRefs(orderId: ID): Promise<DocumentReference[]> {
  const refs: DocumentReference[] = [];
  for (const col of ORDER_CHILD_COLLECTIONS) {
    const snap = await getDocs(query(collection(db, col), where('orderId', '==', orderId)));
    snap.docs.forEach((d) => {
      if (d.data()['isDeleted'] !== true) refs.push(d.ref);
    });
  }
  return refs;
}

/**
 * 受注（親）と、それに内包される見積書・納品書・注文書・請求書・外注支払い・仕入れ（子）を
 * 一括でソフト削除する。
 * - 月次締め処理でロック済みの請求書が紐づく場合は、案件自体を削除できない（throw）。
 * - 親子はまとめて writeBatch でソフト削除し、一覧上に残って不整合が起きるのを防ぐ。
 */
export async function deleteOrder(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('受注が見つかりません。');

  // 月次締めロックのガード：締め済み請求書が紐づく案件は削除不可
  await assertOrderNotLocked(id);

  // 親（受注）に内包される子ドキュメントを収集し、親子まとめてソフト削除する
  const childRefs = await collectOrderChildRefs(id);
  const allRefs = [...childRefs, ref];
  const updateFields = { isDeleted: true, ...buildUpdateFields(userId) };

  for (let i = 0; i < allRefs.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const r of allRefs.slice(i, i + BATCH_LIMIT)) {
      batch.update(r, updateFields);
    }
    await batch.commit();
  }

  await writeAuditLog({
    entityType: 'order',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
    after: { cascadeDeletedChildren: childRefs.length },
  });
}
